import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Fingerprint, Smartphone, Shield } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getSupabaseClient } from '@/lib/supabase/loader';
import { useSession } from '@/hooks/useSession';

interface Anchor {
    id: string;
    type: string;
    icon: React.ReactNode;
    status: "Verified" | "Pending" | "Unverified" | "Unknown";
    lastConfirmed?: string;
    description: string;
}

export const TrustAnchors: React.FC = () => {
    const { user } = useSession();
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [anchors, setAnchors] = useState<Anchor[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                // Defaults
                const base: Record<string, Anchor> = {
                    email: { id: 'email', type: 'Email', icon: <Mail className="w-5 h-5" />, status: 'Unknown', description: 'Primary communication channel for identity recovery.' },
                    phone: { id: 'phone', type: 'Phone', icon: <Phone className="w-5 h-5" />, status: 'Unknown', description: 'SMS based verification for critical changes.' },
                    kyc: { id: 'kyc', type: 'ID/KYC', icon: <Fingerprint className="w-5 h-5" />, status: 'Unknown', description: 'Government issued identity matching your profile.' },
                    devices: { id: 'devices', type: 'Devices', icon: <Smartphone className="w-5 h-5" />, status: 'Unknown', description: 'Authorized hardware keys and trusted devices.' },
                    twofa: { id: 'twofa', type: '2FA', icon: <Shield className="w-5 h-5" />, status: 'Unknown', description: 'Time-based one-time password or passkeys.' },
                };

                // Load supabase client lazily (keep auth calls inside async function)
                const supabase = await getSupabaseClient();

                // Get supabase auth user and profile info
                const { data: authData } = await supabase.auth.getUser();
                const currentUser = user || authData?.user;

                if (!currentUser) {
                    setAnchors(Object.values(base));
                    setLoading(false);
                    return;
                }

                // Email: check email_confirmed_at
                try {
                    const emailVerified = !!(authData?.user?.email_confirmed_at || authData?.user?.email_confirmed_at === null ? false : authData?.user?.email_confirmed_at);
                    base.email.status = emailVerified ? 'Verified' : 'Unverified';
                    if (emailVerified) base.email.lastConfirmed = 'When verified';
                } catch {
                    base.email.status = 'Unknown';
                }

                // Phone: check profiles table for phone presence
                try {
                    const { data: profile } = await supabase.from('profiles').select('phone').eq('id', currentUser.id).single();
                    if (profile?.phone) {
                        // Ideally check sms verification but fallback to Verified when phone exists
                        base.phone.status = 'Verified';
                        base.phone.lastConfirmed = 'Set';
                    } else {
                        base.phone.status = 'Unverified';
                    }
                } catch {
                    base.phone.status = 'Unknown';
                }

                // KYC: check latest kyc_documents for this user
                try {
                    const { data: kyc } = await supabase.from('kyc_documents').select('status, verified_at, created_at').eq('user_id', currentUser.id).order('created_at', { ascending: false }).limit(1).single();
                    if (kyc) {
                        if (kyc.status === 'verified') {
                            base.kyc.status = 'Verified';
                            base.kyc.lastConfirmed = kyc.updated_at || kyc.verified_at || kyc.created_at;
                        } else if (kyc.status === 'submitted' || kyc.status === 'pending') {
                            base.kyc.status = 'Pending';
                        } else {
                            base.kyc.status = 'Unverified';
                        }
                    } else {
                        base.kyc.status = 'Unverified';
                    }
                } catch {
                    base.kyc.status = 'Unknown';
                }

                // Devices: trusted_devices table
                try {
                    const { data: devices } = await supabase.from('trusted_devices').select('is_active, verified_at, last_used_at').eq('user_id', currentUser.id).order('last_used_at', { ascending: false });
                    if (devices && devices.length > 0) {
                        type DeviceRow = { is_active?: boolean; last_used_at?: string };
                        const active = (devices as DeviceRow[]).find((d) => d.is_active);
                        base.devices.status = active ? 'Verified' : 'Pending';
                        base.devices.lastConfirmed = active?.last_used_at || (devices[0] as DeviceRow)?.last_used_at;
                    } else {
                        base.devices.status = 'Unverified';
                    }
                } catch {
                    base.devices.status = 'Unknown';
                }

                // 2FA: try to detect MFA enrollment via auth factors or user metadata
                try {
                    // Some backends store this in user.app_metadata or profiles.has_2fa
                    const has2fa = !!(authData?.user?.app_metadata?.mfa_enabled || (await (await supabase.from('profiles').select('has_2fa').eq('id', currentUser.id).single()).data?.has_2fa));
                    base.twofa.status = has2fa ? 'Verified' : 'Unverified';
                } catch {
                    base.twofa.status = 'Unknown';
                }

                setAnchors([base.email, base.phone, base.kyc, base.devices, base.twofa]);
            } catch (err) {
                console.error('[TrustAnchors] failed to load anchors', err);
            } finally {
                setLoading(false);
            }
        }

        load();
    }, [user]);

    if (loading) {
        return <div className="py-6">Loading anchors…</div>;
    }

    return (
        <div className="flex flex-col items-center py-8">
            <div className="text-center mb-6">
                <h3 className="text-base font-semibold">Your Trust Anchors</h3>
                <p className="text-xs text-muted-foreground">Each anchor strengthens your account’s trustworthiness. Only confirmed anchors are used in critical actions.</p>
            </div>

            <div className="relative w-[260px] h-[260px] flex items-center justify-center">
                {/* Central Core */}
                <div className="absolute w-20 h-20 bg-card border-2 border-white/10 rounded-full flex items-center justify-center z-20 shadow-lg">
                    <Shield className="w-8 h-8 text-primary animate-pulse" />
                </div>

                {/* The Radial spokes */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
                    {anchors.map((anchor, index) => {
                        const angle = (index * (360 / anchors.length)) * (Math.PI / 180);
                        const x1 = 130 + Math.cos(angle) * 30;
                        const y1 = 130 + Math.sin(angle) * 30;
                        const x2 = 130 + Math.cos(angle) * 90;
                        const y2 = 130 + Math.sin(angle) * 90;

                        const isVerified = anchor.status === "Verified";
                        const isPending = anchor.status === "Pending";

                        return (
                            <motion.line
                                key={`line-${anchor.id}`}
                                x1={x1}
                                y1={y1}
                                x2={x2}
                                y2={y2}
                                stroke={isVerified ? "#48bb78" : isPending ? "#ecc94b" : "#4a5568"}
                                strokeWidth="3"
                                strokeLinecap="round"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.7, delay: index * 0.12 }}
                                className={isVerified ? "opacity-100" : "opacity-40"}
                            />
                        );
                    })}
                </svg>

                {/* Anchor Nodes */}
                {anchors.map((anchor, index) => {
                    const angle = (index * (360 / anchors.length)) * (Math.PI / 180);
                    const x = 130 + Math.cos(angle) * 103;
                    const y = 130 + Math.sin(angle) * 103;

                    const isVerified = anchor.status === "Verified";
                    const isPending = anchor.status === "Pending";

                    return (
                        <div
                            key={anchor.id}
                            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-30"
                            style={{ left: x, top: y }}
                            onMouseEnter={() => setHoveredId(anchor.id)}
                            onMouseLeave={() => setHoveredId(null)}
                        >
                            <TooltipProvider>
                                <Tooltip open={hoveredId === anchor.id}>
                                    <TooltipTrigger asChild>
                                        <motion.div
                                            whileHover={{ scale: 1.12 }}
                                            className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-md border-2 
                        ${isVerified ? 'border-green-500 bg-green-500/20 text-green-500' : isPending ? 'border-yellow-500 bg-yellow-500/20 text-yellow-500' : 'border-gray-600 bg-gray-800/40 text-gray-500'}`}
                                        >
                                            {anchor.icon}
                                        </motion.div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="p-3 bg-card border-border shadow-2xl max-w-[240px]">
                                        <div className="space-y-1">
                                            <div className="flex items-center justify-between">
                                                <span className="font-bold text-white">{anchor.type}</span>
                                                <span className={`text-[10px] uppercase px-1 rounded ${isVerified ? 'bg-green-500/20 text-green-500' : isPending ? 'bg-yellow-500/20 text-yellow-500' : 'bg-gray-500/20 text-gray-500'}`}>
                                                    {anchor.status}
                                                </span>
                                            </div>
                                            <p className="text-xs text-muted-foreground">{anchor.description}</p>
                                            {anchor.lastConfirmed && (
                                                <p className="text-[10px] text-primary/60 italic pt-1">Last confirmed: {anchor.lastConfirmed}</p>
                                            )}
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
